/**
 * The Other Stage — heavy EDM vibe with vertical LED towers and lasers.
 */

import * as THREE from 'three';
import { type StageHandle, makeScreenQuad } from './StageBase';

export class OtherStage {
  static build(): StageHandle {
    const group = new THREE.Group();
    group.name = 'OtherStage';

    const matSteel = new THREE.MeshStandardMaterial({
      color: '#15151c',
      roughness: 0.4,
      metalness: 0.85
    });
    const matDeck = new THREE.MeshStandardMaterial({
      color: '#0d0d12',
      roughness: 0.7
    });
    const matScreen = new THREE.MeshBasicMaterial({ color: '#0a0a14' });
    const matLed = new THREE.MeshStandardMaterial({
      color: '#bd72ff',
      emissive: '#bd72ff',
      emissiveIntensity: 1.4
    });
    const matLed2 = new THREE.MeshStandardMaterial({
      color: '#56c2ff',
      emissive: '#56c2ff',
      emissiveIntensity: 1.4
    });

    const deckW = 44;
    const deckD = 18;
    const deckH = 2.2;

    const deck = new THREE.Mesh(new THREE.BoxGeometry(deckW, deckH, deckD), matDeck);
    deck.position.y = deckH / 2;
    deck.castShadow = true;
    deck.receiveShadow = true;
    group.add(deck);

    // LED tower columns
    const towerH = 20;
    for (let i = 0; i < 8; i++) {
      const t = i / 7;
      const x = -deckW / 2 + 1.5 + t * (deckW - 3);
      const tower = new THREE.Mesh(
        new THREE.BoxGeometry(0.6, towerH, 0.6),
        i % 2 === 0 ? matLed : matLed2
      );
      tower.position.set(x, towerH / 2 + deckH, -deckD / 2 + 1.2);
      group.add(tower);
    }

    // Top truss bar
    const truss = new THREE.Mesh(
      new THREE.BoxGeometry(deckW + 2, 0.5, 0.5),
      matSteel
    );
    truss.position.set(0, towerH + deckH + 0.3, 0);
    group.add(truss);

    // Side truss columns
    for (const sign of [-1, 1] as const) {
      const col = new THREE.Mesh(
        new THREE.CylinderGeometry(0.4, 0.4, towerH + 1, 8),
        matSteel
      );
      col.position.set(sign * (deckW / 2 + 0.5), (towerH + 1) / 2 + deckH, 0);
      col.castShadow = true;
      group.add(col);
    }

    // Backdrop screen — wide ratio
    const screenW = 18;
    const screenH = 6;
    const screen = new THREE.Mesh(
      new THREE.PlaneGeometry(screenW, screenH),
      matScreen
    );
    screen.position.set(0, deckH + screenH / 2 + 2, -deckD / 2 + 0.3);
    screen.rotation.y = Math.PI;
    group.add(screen);

    const audioAnchor = new THREE.Object3D();
    audioAnchor.position.set(0, 4, deckD / 2 + 4);
    group.add(audioAnchor);

    return {
      id: 'other-stage',
      label: 'The Other Stage',
      group,
      position: new THREE.Vector3(),
      audioAnchor,
      audioFalloff: { ref: 50, max: 280 },
      screenQuad: makeScreenQuad(
        new THREE.Vector3(0, deckH + screenH / 2 + 2, -deckD / 2 + 0.3),
        screenW,
        screenH,
        Math.PI
      ),
      screenWidth: screenW,
      screenHeight: screenH
    };
  }
}
